import { ApplicationConfig, computed, inject, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { appRoutes } from './app.routes';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  centralHttpErrorInterceptor,
  LayoutService,
  provideCentralErrorHandler,
  provideErrorSnackbar,
  provideLocaleRouting,
  provideLoggingService,
  provideTranslocoService,
  RUNTIME_CONFIGURATION,
  TRANSLATION_SOURCE_REGISTRY,
} from '@processpuzzle/util';
import { RuntimeConfiguration } from './runtime-configuration';
import { CLIPBOARD_OPTIONS, ClipboardButtonComponent, MERMAID_OPTIONS, provideMarkdown } from 'ngx-markdown';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { BASE_WIDGET_ENTITY_FACADES, BASE_WIDGET_FACADE_PROVIDERS, BASE_WIDGET_TRANSLATION_SOURCE, provideAppPropertyStore, provideBaseWidgets } from '@processpuzzle/widgets';
import { AUTHENTICATION_CONFIGURATION, AUTHENTICATION_SERVICE, authTokenInterceptorFor, provideAuthenticationService, provideRoleCatalogSuggestions, ROLE_DESCRIPTION_SOURCE } from '@processpuzzle/auth/domain';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { provideShareButtonsOptions } from 'ngx-sharebuttons';
import { shareIcons } from 'ngx-sharebuttons/icons';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  BASE_ENTITY_AUTHORING_ENTITY_FACADES,
  BASE_ENTITY_AUTHORING_FACADE_PROVIDERS,
  BASE_ENTITY_FACADE_REGISTRY,
  BASE_ENTITY_TRANSLATION_SOURCE,
  provideEntityRouteRegistry,
  provideLocaleDateAdapter,
} from '@processpuzzle/base-entity';
import { BASE_APP_ENTITY_FACADES, BASE_APP_FACADE_PROVIDERS, BASE_APP_TRANSLATION_SOURCE } from '@processpuzzle/base-app';
import {
  BASE_DOCUMENT_ENTITY_FACADES,
  BASE_DOCUMENT_FACADE_PROVIDERS,
  BASE_DOCUMENT_TRANSLATION_SOURCE,
  DOCUMENT_ACCESS_CONTEXT,
  type DocumentAccessContext,
  provideContentBookWidget,
  provideEntityReportsTab,
  provideSignatureWidget,
} from '@processpuzzle/base-document';
import { BASE_STATE_ENTITY_FACADES, BASE_STATE_FACADE_PROVIDERS, BASE_STATE_TRANSLATION_SOURCE, provideEntityStateControl, provideEntityStateMachineTab } from '@processpuzzle/base-state';
import { BASE_EVENT_ENTITY_FACADES, BASE_EVENT_FACADE_PROVIDERS, BASE_EVENT_TRANSLATION_SOURCE } from '@processpuzzle/base-event';
import { BASE_CALCULATION_ENTITY_FACADES, BASE_CALCULATION_FACADE_PROVIDERS, BASE_CALCULATION_TRANSLATION_SOURCE, provideEntityCalculatedControl } from '@processpuzzle/base-calculation';
import { BASE_AI_ENTITY_FACADES, BASE_AI_FACADE_PROVIDERS, BASE_AI_TRANSLATION_SOURCE, provideEntityEnrollmentTab } from '@processpuzzle/base-ai';
import {
  BASE_WORKFLOW_ENTITY_FACADES,
  BASE_WORKFLOW_FACADE_PROVIDERS,
  BASE_WORKFLOW_TRANSLATION_SOURCE,
  WorkflowRoleDescriptionSource,
} from '@processpuzzle/base-workflow';
import { provideBaseRuleEngine } from '@processpuzzle/base-rule';
import { TestEntityFacade } from './content/base-forms/test-entity/test-entity.facade';
import { TestEntityComponentFacade } from './content/base-forms/test-entity-component/test-entity-component.facade';
import { RelatedEntityFacade } from './content/base-forms/related-entity/related-entity.facade';
import { EmbeddedComponentFacade } from './content/base-forms/embedded-component/embedded-component.facade';
import { EmbeddedDetailFacade } from './content/base-forms/embedded-detail/embedded-detail.facade';
import { TrunkDataFacade } from './content/base-forms/trunk-data/trunk-data.facade';
import { provideTestbedApplicationContext } from './testbed-application-context';

/** The signed-in user's roles, for the documents' edit affordances; the backend still decides. */
export function createDocumentAccessContext(): DocumentAccessContext {
  const auth = inject(AUTHENTICATION_SERVICE);
  return { roles: computed(() => auth.currentUser()?.roles ?? []), authenticated: computed(() => auth.currentUser() !== undefined) };
}

export function createAppConfig(runtimeConfiguration: RuntimeConfiguration): ApplicationConfig {
  return {
    providers: [
      provideAuthenticationService(runtimeConfiguration),
      provideZonelessChangeDetection(),
      // The backend validates a bearer token whenever one is sent, and signing a document needs one. Only the
      // platform's own roots are named, and the third-party root (json-server) is withheld explicitly: deployed, it
      // is published under the application's own origin, so the same-origin rule would otherwise send it the token.
      provideHttpClient(
        withInterceptors([
          authTokenInterceptorFor(
            [runtimeConfiguration.BASE_CONFIGURATION.BACKEND_SERVICE_ROOT, runtimeConfiguration.BASE_CONFIGURATION.OBJECT_STORE_SERVICE_ROOT],
            [runtimeConfiguration.BASE_CONFIGURATION.THIRD_PARTY_ROOT],
          ),
          centralHttpErrorInterceptor,
        ]),
      ),
      provideLoggingService(runtimeConfiguration.LOGGING_CONFIGURATION),
      provideCentralErrorHandler(),
      provideAppPropertyStore(),
      TestEntityFacade,
      TestEntityComponentFacade,
      RelatedEntityFacade,
      TrunkDataFacade,
      // Embedded entities are registered like any other: their facade gives them a store, which reads and
      // writes the containing entity's document rather than an endpoint of their own. base-app ships the
      // whole definition graph — the routable `App Definition` and the four embedded levels below it — as
      // one list, so a consuming application cannot register half of it.
      ...BASE_APP_FACADE_PROVIDERS,
      // And for base-entity's own authoring branch — the `Entity Definition` a tenant declares its entity
      // types with, plus the `Entity Attribute` level its form carries. The two entities *below* it in
      // meaning (`Order`, `Dynamic Entity`) are not registered anywhere and must not be; see the comment on
      // BASE_ENTITY_FACADE_REGISTRY.
      ...BASE_ENTITY_AUTHORING_FACADE_PROVIDERS,
      // Same shape for base-document: the routable `Document` plus the two embedded port lists its form
      // carries, which the design section's document route renders.
      ...BASE_DOCUMENT_FACADE_PROVIDERS,
      // And for base-widget: the routable `Widget Definition` plus its two embedded port lists, rendered by
      // the Widgets tab of the design section's Application page.
      ...BASE_WIDGET_FACADE_PROVIDERS,
      // And for base-state: the routable `State Machine Definition` plus the four embedded levels its form
      // carries — states and transitions, and a transition's guards and actions.
      ...BASE_STATE_FACADE_PROVIDERS,
      // And for base-event: the routable `Event Definition` catalog. base-workflow's start event names it in
      // a FOREIGN_KEY without importing base-event, so this registration is also what fills that picker.
      ...BASE_EVENT_FACADE_PROVIDERS,
      // And for base-calculation: the routable `Calculation Definition`, whose extra tabs edit its source.
      ...BASE_CALCULATION_FACADE_PROVIDERS,
      // And for base-workflow: three routable aggregates — the `Workflow` a tenant authors, the
      // `Tool Definition`s its steps call and the `Workflow Instance`s it produces — plus the ten embedded
      // levels below them. All thirteen or none: a task's `performedBy` resolves through the role facade
      // and a step's `toolId` through the tool facade, so half a graph is a form that throws on render.
      ...BASE_WORKFLOW_FACADE_PROVIDERS,
      // And for base-ai: the routable `Recognition Profile`, which says how the subjects of one entity
      // type are recognized.
      ...BASE_AI_FACADE_PROVIDERS,
      // What the header's logo and title widgets name and show; see provideTestbedApplicationContext.
      provideTestbedApplicationContext(),
      // And the other way round: My Profile describes the user's roles from base-workflow's role catalog.
      { provide: ROLE_DESCRIPTION_SOURCE, useExisting: WorkflowRoleDescriptionSource },
      // Role fields on documents and content books offer the organization's role catalog.
      provideRoleCatalogSuggestions(),
      // Adds the read-only State Machine tab to every entity a state machine governs — `Order` and
      // `Dynamic Entity` in this tenant, both of them metadata-defined entities that name base-state
      // nowhere. base-entity asks the registered contributors when it resolves an entity's screens, so
      // this one provider is what puts the tab on all of them; it also registers the `base_state`
      // transloco scope, without which the tab's label would render as its key on those screens.
      ...provideEntityStateMachineTab(),
      ...provideEntityStateControl(),
      // base-calculation does the same for the attributes a released calculation writes (`Order.total`): they render
      // read-only and recompute from the unsaved form instead of offering an edit the backend would discard.
      ...provideEntityCalculatedControl(),
      // The same arrangement for base-ai: the Enrollment tab, offered to every entity type that has a
      // recognition profile — `Boat` in this tenant, a metadata-defined entity that names base-ai nowhere.
      ...provideEntityEnrollmentTab(),
      // And for base-document: the Reports tab, offered to every entity a report template reports on —
      // `Order` in this tenant, through the `order-invoice` sample.
      ...provideEntityReportsTab(),
      // The `signature` widget a report template embeds to be signed; it needs a document's signing context, so
      // base-document registers it rather than provideBaseWidgets().
      ...provideSignatureWidget(),
      // Fills WIDGET_REGISTRY with the components behind the catalogue's keys. base-app's shell renders a
      // widget instance by looking its `type` up there, and provides nothing itself by design — which
      // component answers a key is the hosting application's decision, not the shell's.
      provideBaseWidgets(),
      // base-document's one widget, registered by base-document because it reads documents.
      provideContentBookWidget(),
      // Which document and book actions to offer, from the signed-in user's roles. base-document does not
      // know the auth library, so the application bridges the two; the backend still decides.
      {
        provide: DOCUMENT_ACCESS_CONTEXT,
        useFactory: createDocumentAccessContext,
      },
      // Binds base-entity's `RULE_ENGINE` seam to base-rule's evaluator, for the **whole application**.
      //
      // `BaseEntityFormComponent` injects `RULE_ENGINE` optionally and, when it finds one, loads the rules
      // whose `context` is its descriptor's `entityName` and evaluates them on every change. Nothing else
      // turns rules on: with the token unbound, `loadRules()` returns on its first line and the form is
      // simply unvalidated — silently, because an application with no rule backend is a legitimate
      // deployment (this app runs against json-server too).
      //
      // Which is why this belongs here and not on a route. It used to sit on the `base-rule` route alone,
      // which made rules a property of *which section of the testbed you were in* rather than of the entity
      // being edited: an `Order` form validated under `/base-rule/samples` and the same generated form
      // validated nothing under `/base-entity` or `/design`. Every form loads its own rules, or the feature
      // does not mean anything.
      provideBaseRuleEngine(),
      EmbeddedComponentFacade,
      EmbeddedDetailFacade,
      {
        provide: BASE_ENTITY_FACADE_REGISTRY,
        useValue: {
          'Test Entity': TestEntityFacade,
          'Test Entity Component': TestEntityComponentFacade,
          'Related Entity': RelatedEntityFacade,
          'Trunk Data': TrunkDataFacade,
          // 'Dynamic Entity' with its two embedded levels, and 'Order' with its 'Order Line', are
          // deliberately absent and stay absent: they are metadata — base-entity-backend's
          // processpuzzle-testbed-entities.yaml — and their descriptors are synthesized from those
          // definitions at run-time by base-entity's `EntityScreenResolver`. Registering them here would
          // *override* that, since a compile-time facade wins by design, and the two surfaces that mount
          // them — the Dynamic Entity sample and the demo application's Order screens — exist to exercise
          // the metadata path end to end. An entity only needs an entry below when the application has
          // something to add that a definition cannot express: an extra tab, a hand-tuned layout.
          ...BASE_ENTITY_AUTHORING_ENTITY_FACADES,
          ...BASE_APP_ENTITY_FACADES,
          ...BASE_DOCUMENT_ENTITY_FACADES,
          ...BASE_WIDGET_ENTITY_FACADES,
          ...BASE_STATE_ENTITY_FACADES,
          ...BASE_EVENT_ENTITY_FACADES,
          ...BASE_CALCULATION_ENTITY_FACADES,
          ...BASE_WORKFLOW_ENTITY_FACADES,
          ...BASE_AI_ENTITY_FACADES,
          'Embedded Component': EmbeddedComponentFacade,
          'Embedded Detail': EmbeddedDetailFacade,
        },
      },
      // Which backend serves which transloco scope, for the bundles that have no asset to fall back on.
      // Each library declares its own entry; a scope nobody claims — a designer-authored module's, named
      // at run-time — goes to base-app, which owns ModuleDefinition. All contributions have to be here
      // rather than on route branches: a `multi` token is not merged across injectors.
      ...[
        BASE_APP_TRANSLATION_SOURCE,
        BASE_ENTITY_TRANSLATION_SOURCE,
        BASE_WIDGET_TRANSLATION_SOURCE,
        BASE_DOCUMENT_TRANSLATION_SOURCE,
        BASE_STATE_TRANSLATION_SOURCE,
        BASE_EVENT_TRANSLATION_SOURCE,
        BASE_CALCULATION_TRANSLATION_SOURCE,
        BASE_WORKFLOW_TRANSLATION_SOURCE,
        BASE_AI_TRANSLATION_SOURCE,
      ].map((source) => ({
        provide: TRANSLATION_SOURCE_REGISTRY,
        useValue: source,
        multi: true,
      })),
      { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
      { provide: RUNTIME_CONFIGURATION, useValue: runtimeConfiguration },
      { provide: AUTHENTICATION_CONFIGURATION, useValue: runtimeConfiguration.AUTHENTICATION_CONFIGURATION },
      provideErrorSnackbar(),
      provideRouter(appRoutes, withComponentInputBinding()),
      provideEntityRouteRegistry(),
      provideLocaleDateAdapter(),
      provideShareButtonsOptions(shareIcons()),
      provideTranslocoService(runtimeConfiguration.LANGUAGE_CONFIGURATION),
      // Puts the active language in the URL — `/hu/base-entity` — which is what makes the country-domain
      // redirect in `tools/docker/processpuzzle-testbed-frontend/nginx.conf` mean something: without it
      // `testbed.processpuzzle.hu/x` would land on a `.com` URL that says `hu` and renders English.
      //
      // MUST stay after `provideRouter`, which binds `UrlSerializer` to `DefaultUrlSerializer`; the later
      // provider wins, so moving this line up turns the feature off silently.
      provideLocaleRouting(),
      LayoutService,
      provideMarkdown({
        loader: HttpClient,
        mermaidOptions: {
          provide: MERMAID_OPTIONS,
          useValue: {
            darkMode: true,
            look: 'handDrawn',
          },
        },
        clipboardOptions: {
          provide: CLIPBOARD_OPTIONS,
          useValue: {
            buttonComponent: ClipboardButtonComponent,
          },
        },
      }),
    ],
  };
}
